import styled from 'styled-components';

const Wrapper = styled.div`
  background: var(--clr-background, #f6f8f8);
  min-height: 100vh;

  /* Page Header - Olive Gradient Hero */
  .page-header {
    background: linear-gradient(135deg, var(--clr-olive, #6b7b5c) 0%, #5a6b4d 50%, #4a5a3d 100%);
    padding: 7.5rem 1.5rem 2rem;
    margin-bottom: 0;
    position: relative;
    overflow: hidden;
  }

  @media (min-width: 768px) {
    .page-header {
      padding: 8.5rem 2rem 3rem;
    }
  }

  @media (min-width: 1024px) {
    .page-header {
      padding: 9rem 3rem 4rem;
    }
  }

  /* Decorative elements */
  .page-header::before {
    content: '';
    position: absolute;
    top: -50px;
    right: -50px;
    width: 200px;
    height: 200px;
    border-radius: 50%;
    background: rgba(255, 255, 255, 0.05);
    pointer-events: none;
  }

  .page-header::after {
    content: '';
    position: absolute;
    bottom: -80px;
    left: -80px;
    width: 250px;
    height: 250px;
    border-radius: 50%;
    background: rgba(201, 169, 98, 0.08);
    pointer-events: none;
  }

  .page-header .header-content {
    max-width: 1440px;
    margin: 0 auto;
    position: relative;
    z-index: 1;
  }

  .page-header .breadcrumb {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    font-size: 0.75rem;
    color: rgba(255, 255, 255, 0.7);
    margin-bottom: 0.75rem;
  }

  @media (min-width: 768px) {
    .page-header .breadcrumb {
      font-size: 0.875rem;
      margin-bottom: 1rem;
    }
  }

  .page-header .breadcrumb a {
    color: rgba(255, 255, 255, 0.7);
    text-decoration: none;
    transition: color 0.2s;
  }

  .page-header .breadcrumb a:hover {
    color: #ffffff;
  }

  .page-header .breadcrumb span:last-child {
    color: #ffffff;
  }

  .page-header .breadcrumb svg {
    width: 12px;
    height: 12px;
    opacity: 0.5;
  }

  .page-header h1 {
    font-family: 'Playfair Display', var(--font-display), serif;
    font-size: 1.75rem;
    font-weight: 600;
    color: #ffffff;
    margin: 0 0 0.5rem 0;
  }

  @media (min-width: 768px) {
    .page-header h1 {
      font-size: 2.75rem;
      margin-bottom: 0.75rem;
    }
  }

  @media (min-width: 1024px) {
    .page-header h1 {
      font-size: 3.25rem;
    }
  }

  .page-header .subtitle {
    display: none;
    font-size: 1rem;
    color: rgba(255, 255, 255, 0.8);
    max-width: 500px;
    margin: 0;
  }

  @media (min-width: 768px) {
    .page-header .subtitle {
      display: block;
    }
  }

  /* Mobile Category Pills */
  .mobile-categories {
    display: block;
    position: sticky;
    top: 56px;
    z-index: 30;
    background: #ffffff;
    border-bottom: 1px solid rgba(0, 0, 0, 0.06);
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
  }

  @media (min-width: 992px) {
    .mobile-categories {
      display: none;
    }
  }

  .categories-scroll {
    overflow-x: auto;
    -webkit-overflow-scrolling: touch;
    scrollbar-width: none;
    -ms-overflow-style: none;
  }

  .categories-scroll::-webkit-scrollbar {
    display: none;
  }

  .categories-pills {
    display: flex;
    gap: 0.5rem;
    padding: 0.75rem 1rem;
    min-width: max-content;
  }

  .category-pill {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    padding: 0.5rem 0.875rem;
    background: rgba(0, 0, 0, 0.04);
    border: none;
    border-radius: 50px;
    font-family: var(--font-body), system-ui, sans-serif;
    font-size: 0.8125rem;
    font-weight: 500;
    color: #6b7280;
    white-space: nowrap;
    cursor: pointer;
    transition: all 0.2s ease;
    -webkit-tap-highlight-color: transparent;
  }

  .category-pill:active {
    transform: scale(0.95);
  }

  .category-pill.active {
    background: var(--clr-olive, #6b7b5c);
    color: #ffffff;
    box-shadow: 0 2px 8px rgba(107, 123, 92, 0.3);
  }

  .category-pill .pill-icon {
    font-size: 0.875rem;
  }

  .category-pill .pill-count {
    font-size: 0.6875rem;
    padding: 0.125rem 0.375rem;
    background: rgba(0, 0, 0, 0.08);
    border-radius: 50px;
  }

  .category-pill.active .pill-count {
    background: rgba(255, 255, 255, 0.2);
  }

  /* Main Content */
  .products-container {
    max-width: 1440px;
    margin: 0 auto;
    padding: 1.5rem 1rem;
  }

  @media (min-width: 768px) {
    .products-container {
      padding: 2rem 1.5rem;
    }
  }

  @media (min-width: 1024px) {
    .products-container {
      padding: 2.5rem 2rem;
    }
  }

  .products {
    display: flex;
    flex-direction: column;
    gap: 2rem;
  }

  @media (min-width: 1024px) {
    .products {
      flex-direction: row;
      gap: 2.5rem;
    }
  }

  /* Sidebar / Filters */
  .filters-section {
    display: none;
  }

  @media (min-width: 1024px) {
    .filters-section {
      display: block;
      width: 260px;
      flex-shrink: 0;
    }
  }

  .filters-sticky {
    position: sticky;
    top: 100px;
  }

  /* Products Section */
  .products-section {
    flex: 1;
    min-width: 0;
  }

  /* Mobile Filter Button */
  .mobile-filter-btn {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    padding: 0.625rem 1rem;
    background: #ffffff;
    border: 1px solid #e5e7eb;
    border-radius: 0.5rem;
    font-family: var(--font-body), system-ui, sans-serif;
    font-size: 0.875rem;
    font-weight: 500;
    color: #374151;
    cursor: pointer;
    transition: all 0.2s;
    position: relative;
  }

  .mobile-filter-btn:hover {
    border-color: var(--clr-olive, #6b7b5c);
  }

  .mobile-filter-btn .filter-badge {
    position: absolute;
    top: -6px;
    right: -6px;
    min-width: 18px;
    height: 18px;
    background: var(--clr-olive, #6b7b5c);
    color: #ffffff;
    font-size: 0.6875rem;
    font-weight: 600;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 0 4px;
  }

  @media (min-width: 1024px) {
    .mobile-filter-btn {
      display: none;
    }
  }

  /* Mobile Filter Bottom Sheet */
  .filter-overlay {
    position: fixed;
    inset: 0;
    background: rgba(0, 0, 0, 0.5);
    z-index: 100;
    opacity: 0;
    visibility: hidden;
    transition: all 0.3s ease;
  }

  .filter-overlay.active {
    opacity: 1;
    visibility: visible;
  }

  .filter-sheet {
    position: fixed;
    left: 0;
    right: 0;
    bottom: 0;
    max-height: 85vh;
    background: #ffffff;
    border-radius: 24px 24px 0 0;
    z-index: 101;
    transform: translateY(100%);
    transition: transform 0.3s cubic-bezier(0.32, 0.72, 0, 1);
    overflow: hidden;
    display: flex;
    flex-direction: column;
  }

  .filter-sheet.active {
    transform: translateY(0);
  }

  .sheet-handle {
    display: flex;
    justify-content: center;
    padding: 0.75rem 0 0.5rem;
    flex-shrink: 0;
  }

  .sheet-handle span {
    width: 40px;
    height: 4px;
    background: rgba(0, 0, 0, 0.15);
    border-radius: 2px;
  }

  .sheet-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 0.5rem 1.25rem 1rem;
    border-bottom: 1px solid rgba(0, 0, 0, 0.06);
    flex-shrink: 0;
  }

  .sheet-header h3 {
    font-family: 'Playfair Display', var(--font-display), serif;
    font-size: 1.25rem;
    font-weight: 600;
    color: #111718;
    margin: 0;
  }

  .sheet-close {
    width: 32px;
    height: 32px;
    display: flex;
    align-items: center;
    justify-content: center;
    background: rgba(0, 0, 0, 0.05);
    border: none;
    border-radius: 50%;
    color: #6b7280;
    cursor: pointer;
    transition: all 0.2s;
  }

  .sheet-close:hover {
    background: rgba(0, 0, 0, 0.1);
    color: #374151;
  }

  .sheet-content {
    flex: 1;
    overflow-y: auto;
    padding: 1.25rem;
    -webkit-overflow-scrolling: touch;
  }

  .sheet-footer {
    padding: 1rem 1.25rem;
    padding-bottom: max(1rem, env(safe-area-inset-bottom));
    border-top: 1px solid rgba(0, 0, 0, 0.06);
    display: flex;
    gap: 0.75rem;
    flex-shrink: 0;
    background: #ffffff;
  }

  .sheet-btn {
    flex: 1;
    padding: 0.875rem 1rem;
    border-radius: 0.5rem;
    font-family: var(--font-body), system-ui, sans-serif;
    font-size: 0.9375rem;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.2s;
  }

  .sheet-btn.outline {
    background: transparent;
    border: 1px solid var(--clr-olive, #6b7b5c);
    color: var(--clr-olive, #6b7b5c);
  }

  .sheet-btn.outline:hover {
    background: rgba(107, 123, 92, 0.05);
  }

  .sheet-btn.primary {
    background: var(--clr-olive, #6b7b5c);
    border: 1px solid var(--clr-olive, #6b7b5c);
    color: #ffffff;
  }

  .sheet-btn.primary:hover {
    background: #5a6a4c;
    border-color: #5a6a4c;
  }

  /* Sheet categories grid */
  .sheet-categories {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 0.5rem;
    margin-bottom: 1.5rem;
  }

  .sheet-category {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 0.375rem;
    padding: 0.875rem 0.5rem;
    background: rgba(0, 0, 0, 0.03);
    border: none;
    border-radius: 0.75rem;
    cursor: pointer;
    transition: all 0.2s;
    font-family: var(--font-body), system-ui, sans-serif;
  }

  .sheet-category:active {
    transform: scale(0.95);
  }

  .sheet-category.active {
    background: var(--clr-olive, #6b7b5c);
    color: #ffffff;
    box-shadow: 0 4px 12px rgba(107, 123, 92, 0.25);
  }

  .sheet-category .cat-icon {
    font-size: 1.25rem;
  }

  .sheet-category .cat-name {
    font-size: 0.75rem;
    font-weight: 500;
    text-align: center;
  }

  /* Sheet section */
  .sheet-section {
    margin-bottom: 1.5rem;
  }

  .sheet-section h4 {
    font-family: var(--font-body), system-ui, sans-serif;
    font-size: 0.875rem;
    font-weight: 600;
    color: #111718;
    margin: 0 0 0.75rem 0;
  }

  /* Colors in sheet */
  .sheet-colors {
    display: flex;
    flex-wrap: wrap;
    gap: 0.75rem;
  }

  .sheet-color {
    width: 40px;
    height: 40px;
    border-radius: 50%;
    border: 2px solid transparent;
    cursor: pointer;
    transition: all 0.2s;
    position: relative;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .sheet-color.white {
    border-color: #e5e7eb;
  }

  .sheet-color.active {
    box-shadow: 0 0 0 3px var(--clr-olive, #6b7b5c);
  }

  .sheet-color svg {
    color: #ffffff;
  }

  .sheet-color.white svg,
  .sheet-color.beige svg {
    color: #374151;
  }

  /* Price slider in sheet */
  .sheet-price {
    padding: 0 0.25rem;
  }

  .sheet-price .price-slider {
    width: 100%;
    height: 4px;
    background: #e5e7eb;
    border-radius: 2px;
    -webkit-appearance: none;
    appearance: none;
    cursor: pointer;
    margin-bottom: 0.75rem;
  }

  .sheet-price .price-slider::-webkit-slider-thumb {
    -webkit-appearance: none;
    appearance: none;
    width: 20px;
    height: 20px;
    background: var(--clr-olive, #6b7b5c);
    border-radius: 50%;
    cursor: pointer;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.15);
  }

  .sheet-price .price-slider::-moz-range-thumb {
    width: 20px;
    height: 20px;
    background: var(--clr-olive, #6b7b5c);
    border-radius: 50%;
    cursor: pointer;
    border: none;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.15);
  }

  .sheet-price .price-labels {
    display: flex;
    justify-content: space-between;
  }

  .sheet-price .price-label {
    font-size: 0.875rem;
    font-weight: 500;
    color: #111718;
  }

  /* Free shipping in sheet */
  .sheet-shipping {
    display: flex;
    align-items: center;
    gap: 0.75rem;
    padding: 1rem;
    background: rgba(0, 0, 0, 0.03);
    border-radius: 0.75rem;
    cursor: pointer;
  }

  .sheet-shipping input[type="checkbox"] {
    width: 20px;
    height: 20px;
    accent-color: var(--clr-olive, #6b7b5c);
    cursor: pointer;
  }

  .sheet-shipping label {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    font-size: 0.9375rem;
    color: #374151;
    cursor: pointer;
    flex: 1;
  }

  .sheet-shipping svg {
    color: var(--clr-olive, #6b7b5c);
  }

  @media (min-width: 1024px) {
    .filter-overlay,
    .filter-sheet {
      display: none;
    }
  }
`;

export default Wrapper;
